import { useEffect, useId, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { AdultQuestionView } from './component-details/parts/AdultQuestionView'
import { AnswerEditButton, AnswerEditCancelButton } from './component-details/parts/AnswerEditButton'
import { isSubjectiveQuizType } from './component-details/parts/SubjectiveGradeSelect'
import { hasSubmittedAnswer, toAnswerArray } from './component-details/parts/answerArray'
import { useCoachingEditorState } from './CoachingEditorContext'
import type { QuizFocusRequest } from './grading-sheet/coachingFocus'

export interface QuestionGradingItem {
  quizId: string
  gradedIsCorrect: boolean | null
  studentAnswer: unknown
  corrects?: (boolean | null)[] | null
  questionSnapshot: Record<string, unknown> | null
  correctAnswer: unknown
  explanation: string | null
}

export interface QuestionGradeSubmission {
  quizId: string
  answers: string[]
  isCorrect?: boolean
  manualGrade?: boolean
}

interface Props {
  items: readonly QuestionGradingItem[]
  onGrade?: (submission: QuestionGradeSubmission) => void | Promise<unknown>
  isSubmitting?: boolean
  canGrade?: boolean
  emptyMessage?: ReactNode
  presentation?: 'list' | 'single'
  resultHideSolveDescription?: boolean
  navigationActions?: ReactNode
  renderItemActions?: (item: QuestionGradingItem, index: number) => ReactNode
  /** single 프레젠테이션에서 처음 보여줄 문항(채점표 [답안 편집] 바로가기). 미지정이면 첫 문항. */
  initialSelectedQuizId?: string | null
  /** 마운트 이후 도착한 선택 요청 — nonce가 바뀌면 해당 문항으로 이동 */
  focusRequest?: QuizFocusRequest | null
}

export function QuestionGradingList({
  items,
  onGrade,
  isSubmitting = false,
  canGrade = true,
  emptyMessage,
  presentation = 'list',
  resultHideSolveDescription = false,
  navigationActions,
  renderItemActions,
  initialSelectedQuizId = null,
  focusRequest = null,
}: Props) {
  const { t } = useTranslation('coaching')
  const editorId = useId()
  const [answerDrafts, setAnswerDrafts] = useState<Record<string, string[]>>({})
  const hasAnswerDrafts = items.some((item) => {
    const draft = answerDrafts[item.quizId]
    if (draft == null) return false
    const saved = toAnswerArray(item.studentAnswer)
    if (!hasSubmittedAnswer(saved) && !hasSubmittedAnswer(draft)) return false
    return saved.length !== draft.length || saved.some((value, index) => value !== draft[index])
  })
  useCoachingEditorState(`question:${editorId}`, hasAnswerDrafts, isSubmitting)
  const [editingQuizIds, setEditingQuizIds] = useState<Set<string>>(new Set())
  const [locallySubmittedIds, setLocallySubmittedIds] = useState<Set<string>>(new Set())
  const [selectedQuizId, setSelectedQuizId] = useState<string | null>(initialSelectedQuizId)
  const focusQuizId = focusRequest?.quizId ?? null
  const focusNonce = focusRequest?.nonce ?? null
  useEffect(() => {
    if (focusQuizId != null && focusNonce != null) setSelectedQuizId(focusQuizId)
  }, [focusQuizId, focusNonce])

  if (items.length === 0) {
    return <p className="p-6 text-sm text-gray-500">{emptyMessage ?? t('test.empty')}</p>
  }

  const startEditing = (quizId: string) => {
    setEditingQuizIds((previous) => new Set(previous).add(quizId))
  }

  const cancelEditing = (quizId: string) => {
    setEditingQuizIds((previous) => {
      const next = new Set(previous)
      next.delete(quizId)
      return next
    })
  }

  const finishEditing = (quizId: string) => {
    setAnswerDrafts((current) => { const next = { ...current }; delete next[quizId]; return next })
    setEditingQuizIds((previous) => {
      const next = new Set(previous)
      next.delete(quizId)
      return next
    })
    setLocallySubmittedIds((previous) => new Set(previous).add(quizId))
  }

  const submitGrade = (submission: QuestionGradeSubmission) => {
    if (onGrade == null) return
    const pending = onGrade(submission)
    if (pending) {
      void pending.then(
        () => finishEditing(submission.quizId),
        () => undefined,
      )
      return
    }
    finishEditing(submission.quizId)
  }

  const requestedIndex = selectedQuizId == null
    ? -1
    : items.findIndex((item) => item.quizId === selectedQuizId)
  const selectedIndex = requestedIndex >= 0 ? requestedIndex : 0
  const visibleItems = presentation === 'single'
    ? [{ item: items[selectedIndex], index: selectedIndex }]
    : items.map((item, index) => ({ item, index }))

  const moveTo = (index: number) => {
    const nextItem = items[index]
    if (nextItem) setSelectedQuizId(nextItem.quizId)
  }

  return (
    <div>
      {presentation === 'single' ? (
        <nav
          aria-label={t('test.questionNavigation')}
          className="overflow-x-auto border-b border-gray-200 bg-gray-50/80 px-4 py-3"
        >
          <div className="flex min-w-max items-center gap-2">
            {items.map((item, index) => {
              const status = item.gradedIsCorrect == null
                ? 'notGraded'
                : item.gradedIsCorrect
                  ? 'correct'
                  : 'incorrect'
              return (
                <button
                  key={item.quizId}
                  type="button"
                  aria-current={selectedIndex === index ? 'true' : undefined}
                  aria-label={t('test.questionChipAria', {
                    no: index + 1,
                    status: t(`test.questionStatus.${status}`),
                  })}
                  onClick={() => setSelectedQuizId(item.quizId)}
                  className={clsx(
                    'relative inline-flex size-9 shrink-0 items-center justify-center rounded-full border text-sm font-bold tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                    status === 'correct' && 'border-lc-mint-300 bg-lc-mint-100 text-lc-mint-900',
                    status === 'incorrect' && 'border-red-200 bg-red-50 text-red-700',
                    status === 'notGraded' && 'border-gray-300 bg-white text-gray-600',
                    selectedIndex === index && 'ring-2 ring-primary-500 ring-offset-2',
                  )}
                >
                  {index + 1}
                  <span
                    aria-hidden="true"
                    className={clsx(
                      'absolute -right-0.5 -top-0.5 flex size-3.5 items-center justify-center rounded-full text-[9px] font-black leading-none',
                      status === 'correct' && 'bg-lc-mint-100 text-lc-mint-900 ring-1 ring-inset ring-lc-mint-300',
                      status === 'incorrect' && 'bg-red-500 text-white',
                      status === 'notGraded' && 'bg-gray-300 text-white',
                    )}
                  >
                    {status === 'correct' ? '✓' : status === 'incorrect' ? '×' : '·'}
                  </span>
                </button>
              )
            })}
            {navigationActions}
          </div>
        </nav>
      ) : null}

      <ol className={clsx(presentation === 'list' && 'divide-y divide-gray-100')}>
        {visibleItems.map(({ item, index }) => {
          const hasAnswer = hasSubmittedAnswer(item.studentAnswer) || locallySubmittedIds.has(item.quizId)
          const editing = canGrade && onGrade != null && (editingQuizIds.has(item.quizId) || !hasAnswer)
          const isSubjective = isSubjectiveQuizType(item.questionSnapshot)

          return (
            <li key={item.quizId} className="space-y-3 p-4" data-testid="test-item">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-gray-500">
                    {t('test.itemNo', { no: index + 1 })}
                  </span>
                  <span
                    className={clsx(
                      'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
                      item.gradedIsCorrect == null
                        ? 'bg-gray-100 text-gray-600'
                        : item.gradedIsCorrect
                          ? 'bg-lc-mint-100 text-lc-mint-900'
                          : 'bg-red-50 text-red-700',
                    )}
                  >
                    {item.gradedIsCorrect == null
                      ? t('test.notGraded')
                      : item.gradedIsCorrect
                        ? t('quiz.correct')
                        : t('quiz.incorrect')}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {renderItemActions?.(item, index)}
                  {canGrade && onGrade != null && hasAnswer ? (
                    editing ? (
                      <AnswerEditCancelButton
                        onClick={() => cancelEditing(item.quizId)}
                        disabled={isSubmitting}
                      />
                    ) : (
                      <AnswerEditButton
                        onClick={() => startEditing(item.quizId)}
                        disabled={isSubmitting}
                      />
                    )
                  ) : null}
                </div>
              </div>

              {editing ? (
                <AdultQuestionView
                  key={`grading-solve-${item.quizId}-${isSubjective ? 'subjective' : 'auto'}`}
                  quizId={item.quizId}
                  order={index}
                  mode="solve"
                  isCorrect={item.gradedIsCorrect ?? undefined}
                  questionSnapshot={item.questionSnapshot ?? undefined}
                  studentAnswer={answerDrafts[item.quizId] ?? item.studentAnswer}
                  onAnswerChange={(answers) => setAnswerDrafts((current) => ({ ...current, [item.quizId]: answers }))}
                  corrects={item.corrects ?? undefined}
                  correctAnswer={item.correctAnswer}
                  explanation={item.explanation ?? undefined}
                  onSolve={isSubjective ? undefined : (answers) => {
                    submitGrade({ quizId: item.quizId, answers })
                  }}
                  onSubjectiveGrade={isSubjective ? ({ answers, isCorrect }) => {
                    submitGrade({
                      quizId: item.quizId,
                      answers,
                      isCorrect,
                      manualGrade: true,
                    })
                  } : undefined}
                  isSubmitting={isSubmitting}
                />
              ) : (
                <AdultQuestionView
                  key={`grading-result-${item.quizId}`}
                  quizId={item.quizId}
                  order={index}
                  mode="result"
                  isCorrect={item.gradedIsCorrect ?? undefined}
                  questionSnapshot={item.questionSnapshot ?? undefined}
                  studentAnswer={item.studentAnswer}
                  corrects={item.corrects ?? undefined}
                  correctAnswer={item.correctAnswer}
                  explanation={item.explanation ?? undefined}
                  hideSolveDescription={resultHideSolveDescription}
                />
              )}
            </li>
          )
        })}
      </ol>

      {presentation === 'single' ? (
        <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3">
          <button
            type="button"
            disabled={selectedIndex === 0}
            onClick={() => moveTo(selectedIndex - 1)}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {t('test.previousQuestion')}
          </button>
          <span className="text-xs font-semibold tabular-nums text-gray-500">
            {t('test.questionProgress', { current: selectedIndex + 1, total: items.length })}
          </span>
          <button
            type="button"
            disabled={selectedIndex === items.length - 1}
            onClick={() => moveTo(selectedIndex + 1)}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {t('test.nextQuestion')}
          </button>
        </div>
      ) : null}
    </div>
  )
}
